import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ENV_CONFIG } from 'config/environment';
import { formatDate, toDateTimeString } from 'helpers/date';
import { required, transformToUrl } from 'helpers/utils';
import { websiteWithLines } from 'helpers/regexp';
import { IClientPages, IFieldRestrictions, IPage } from 'store/pages/types';
import { AllocationGroup } from './Forms/DonationAllocation/types';
import {
  CUSTOM_ALLOCATION_TYPES,
  GROUP_LABEL_MAX_LENGTH,
} from './Forms/DonationAllocation/DonationAllocationForm/constants';
import { RESTRICTED_FIELD_DEFAULTS, RESTRICTED_FIELD_LABELS } from './constants';
import { IRecipientState } from 'store/recipients/types';
import { SetsData } from 'store/sets/types';
import { createEmptySection } from './Forms/SectionsForm/utils';
import { has_permission } from 'config/permissions';
import { tableButton } from 'components/CustomTable';
import { DashboardScreen, PageCategory, trackPageRowActionClicked } from 'helpers/mixpanelEvents';

const SCREEN: DashboardScreen = 'pages';

/**
 * Prices shared by more than one active ticket; the source of truth for both the
 * submit-time validation and the in-table cell highlight.
 */
export const getDuplicateTicketPrices = (rows: any): Set<number> => {
  const prices = (Array.isArray(rows) ? rows : [])
    .filter(row => row.status === 'ACTIVE')
    .map(row => row.price);
  return new Set(prices.filter((price, index) => prices.indexOf(price) !== index));
};

export const validateEventForm = (values: any): Record<string, string> => {
  const activeRows = (values.rows || []).filter((row: any) => row.status === 'ACTIVE');
  if (activeRows.some((row: any) => row.quantity <= 0)) {
    return { rows: 'Each ticket must have a quantity greater than 0.' };
  }
  if (getDuplicateTicketPrices(values.rows).size > 0) {
    return { rows: 'Each ticket must have a unique price.' };
  }
  return {};
};

export const validateAllocationForm = (values: any, pageType: string): Record<string, string> => {
  const isAllocationPage = pageType === 'donate' || pageType === 'event';
  if (!isAllocationPage) return {};

  const { allocation_type, recipient_groups } = values;
  if (!CUSTOM_ALLOCATION_TYPES.includes(allocation_type)) return {};

  if (values.recurring_enabled) {
    return {
      recipient_groups:
        'Custom allocation types are not available on pages with recurring payments enabled.',
    };
  }

  const groups = recipient_groups || [];

  if (groups.length < 2) {
    return { recipient_groups: 'At least 2 groups are required.' };
  }
  if (groups.length > 6) {
    return { recipient_groups: 'You can only have up to 6 groups.' };
  }
  if (groups.some((g: any) => g.recipient_ids.length === 0)) {
    return { recipient_groups: 'All groups must have at least one recipient.' };
  }
  if (allocation_type === 'WEIGHTED') {
    if (groups.some((g: any) => (g.weight_percent || 0) < 10 || (g.weight_percent || 0) > 100)) {
      return { recipient_groups: 'All groups must have a weight between 10% and 100%.' };
    }
    const total = groups.reduce((sum: number, g: any) => sum + (g.weight_percent || 0), 0);
    if (total !== 100) {
      return { recipient_groups: `Group weights must sum to 100% (current total: ${total}%).` };
    }
  }
  if (allocation_type === 'WATERFALL') {
    if (
      groups.slice(0, -1).some((g: any) => !g.donation_limit_cents || g.donation_limit_cents <= 0)
    ) {
      return { recipient_groups: 'All groups must have an allocation amount greater than $0.' };
    }
  }
  if (groups.some((g: any) => (g.group_label?.trim().length ?? 0) > GROUP_LABEL_MAX_LENGTH)) {
    return {
      recipient_groups: `Group labels must be ${GROUP_LABEL_MAX_LENGTH} characters or fewer.`,
    };
  }
  return {};
};

export const buildRecipientGroupsPayload = (
  groups: AllocationGroup[] | undefined,
  allocationType: string
) =>
  (groups || []).map((g, index) => ({
    id: g.id,
    page_id: g.page_id,
    weight_percent: allocationType === 'WEIGHTED' ? g.weight_percent : null,
    recipient_ids: g.recipient_ids,
    priority: index + 1,
    donation_limit_cents: g.donation_limit_cents ?? null,
    group_label: g.group_label?.trim() || null,
  }));

type OfflinePaymentValues = {
  wire_enabled?: boolean;
  offline_instructions_wire?: { label: string; value: string }[];
  offline_instructions_wire_enabled?: boolean;
  offline_instructions_check?: { label: string; value: string }[];
  offline_instructions_check_enabled?: boolean;
  offline_instructions_grant?: { label: string; value: string }[];
  offline_instructions_grant_enabled?: boolean;
};

export const buildOfflinePaymentPayload = (values: OfflinePaymentValues) => ({
  wire_enabled: values.wire_enabled,
  offline_instructions_wire: values.offline_instructions_wire_enabled
    ? (values.offline_instructions_wire ?? [])
    : [],
  offline_instructions_check: values.offline_instructions_check_enabled
    ? (values.offline_instructions_check ?? [])
    : [],
  offline_instructions_grant: values.offline_instructions_grant_enabled
    ? (values.offline_instructions_grant ?? [])
    : [],
});

export const buildSuperOnlyPageFields = (values: OfflinePaymentValues, isSuper: boolean) =>
  isSuper ? buildOfflinePaymentPayload(values) : {};

export const getInitialValues = (
  editPageSelected?: IPage,
  recipientsState?: IRecipientState,
  isPageDuplicated?: boolean,
  clientPages?: IClientPages
) => {
  if (!editPageSelected) {
    return {
      page_name: '',
      query_string: '',
      description: '',
      donation_amounts: [10, 25, 50, 100, 250],
      page_cta: '',
      page_background: '',
      page_color_text: '',
      impact_score_visibility: false,
      show_more_visibility: true,
      page_type: 'LIST',
      recipients_selected: [],
      selectedFile: undefined,
      preview: '',
      communityPreview: '',
      selectedCommunityFile: undefined,
      subdescription: '',
      location_name: '',
      is_location_visible: false,
      event_date: '',
      start_time: '',
      end_time: '',
      doors_open_before_event: false,
      doors_open_datetime: '',
      ticket_arrangement: 'MANUALLY',
      rows: [
        {
          id: '1',
          name: '',
          price: 0,
          quantity: 999,
          quantity_remaining: 999,
          status: 'ACTIVE',
          description: '',
          order: 0,
        },
      ],
      recipient_details: {},
      subtitle: '',
      custom_disclaimer: '',
      page_status: true,
      whitelabeled: false,
      status: '',
      active_page_status: true,
      paused_description: '',
      paused_redirect_button: '',
      paused_redirect_url: '',
      page_category: '',
      display_fundraising_bar: true,
      learn_more_link: '',
      bottom_url_text: '',
      fundraising_goal: 1000,
      processing_redirect: clientPages?.preferred_payment_processor || 'STRIPE',
      is_public_searchable: false,
      recipient_order_by: '',
      is_oath_disclaimer_visible: true,
      recurring_enabled: false,
      recurring_interval: null,
      wire_enabled: false,
      offline_instructions_wire: [],
      offline_instructions_wire_enabled: false,
      offline_instructions_check: [],
      offline_instructions_check_enabled: false,
      offline_instructions_grant: [],
      offline_instructions_grant_enabled: false,
      optional_file_arrangement: 'BELOW_PAGE_NAME',
      previewOptionalMedia: '',
      selectedOptionalMediaFile: undefined,
      allocation_type: 'EVEN',
      recipient_groups: [],
    };
  }

  const page = editPageSelected[0];
  const recipients = recipientsState?.data?.recipients || [];

  return {
    page_name: isPageDuplicated ? `${page.value} Copy` : page.value,
    query_string: isPageDuplicated ? `${page.query_str}-copy` : page.query_str,
    description: page.desc,
    donation_amounts: page.donation_amounts?.map(amount => amount / 100) || [],
    page_cta: page.page_cta,
    page_background: page?.page_background,
    page_color_text: page?.page_color_text,
    impact_score_visibility: page.impact_score_visibility,
    show_more_visibility: page.show_more_visibility,
    page_type: page.page_type || 'LIST',
    recipients_selected:
      page.recipient_ids?.reduce((acc, id) => {
        const recipient = recipients?.find(rec => rec.recipient_id === id);
        if (recipient) {
          acc.push({
            value: id,
            label: recipient?.recipient_name || `Recipient ${id}`,
          });
        }
        return acc;
      }, []) || [],
    ...((): {
      preview: string | false;
      communityPreview: string | false;
      previewOptionalMedia: string | undefined;
    } => {
      const gcsTagsBase = `${ENV_CONFIG().CLOUD_STORAGE.URL}/${ENV_CONFIG().CLOUD_STORAGE.TAGS}`;
      const imageFilenames = page?.image_filenames;
      return {
        preview:
          page?.has_image &&
          (imageFilenames?.principal_logo
            ? `${gcsTagsBase}/${imageFilenames.principal_logo}`
            : `${gcsTagsBase}/tag_${page.id}.png`),
        communityPreview:
          page?.has_community_image &&
          (imageFilenames?.community_logo
            ? `${gcsTagsBase}/icon/${imageFilenames.community_logo}`
            : `${gcsTagsBase}/icon/tag_${page.id}.png`),
        previewOptionalMedia: page?.has_optional_media
          ? imageFilenames?.optional_media
            ? `${gcsTagsBase}/optional_media/${imageFilenames.optional_media}`
            : `${gcsTagsBase}/optional_media/tag_${page.id}.${page.optional_media_type === 'video' ? 'mp4' : 'png'}`
          : undefined,
      };
    })(),
    optional_media_type: page.optional_media_type ?? null,
    optional_file_arrangement: page.optional_file_arrangement || 'BELOW_PAGE_NAME',
    selectedCommunityFile: undefined,
    selectedFile: undefined,
    selectedOptionalMediaFile: undefined,
    ...(page.event_id && {
      subdescription: page.subdescription,
      location_name: page.location_name,
      is_location_visible: page.is_location_visible,
      doors_open_before_event: !!page.doors_open_datetime,
      doors_open_datetime: formatDate(page.doors_open_datetime, 'h:mm A'),
      event_date: formatDate(page.start_datetime, 'MM/DD/YYYY'),
      start_time: formatDate(page.start_datetime, 'h:mm A'),
      end_time: page.end_datetime ? formatDate(page.end_datetime, 'h:mm A') : '',
      rows:
        page.tickets?.length > 0
          ? page.tickets.map((ticket, index) => ({
              ...ticket,
              id: isPageDuplicated ? `copy-${index}` : ticket.id,
              price: ticket.price / 100,
            }))
          : [
              {
                id: '1',
                name: '',
                price: 0,
                quantity: 999,
                quantity_remaining: 999,
                status: 'ACTIVE',
                description: '',
                order: 0,
              },
            ],
      event_id: isPageDuplicated ? null : page.event_id,
      ticket_arrangement: page.ticket_arrangement || 'MANUALLY',
    }),
    recipient_details: page.recipient_details,
    subtitle: page.subtitle,
    custom_disclaimer: page.custom_disclaimer || '',
    page_status: page.page_status || true,
    whitelabeled: page.whitelabeled ? 'TRUE' : 'FALSE',
    status: page.status,
    active_page_status: page.status !== 'PAUSED',
    paused_misc: page.paused_misc,
    paused_description: page.paused_misc?.paused_description || '',
    paused_redirect_button: page.paused_misc?.paused_redirect_button || '',
    paused_redirect_url: page.paused_misc?.paused_redirect_url || '',
    page_category: page.page_category,
    display_fundraising_bar: page.display_fundraising_bar ? 'TRUE' : 'FALSE',
    fundraising_goal: page.fundraising_goal / 100 || 1000,
    learn_more_link: page.learn_more_link || '',
    bottom_url_text: page.bottom_url_text || '',
    processing_redirect: page.processing_redirect || 'DE_OATH',
    is_public_searchable: page.is_public_searchable || false,
    recipient_order_by: page.recipient_order_by || '',
    is_oath_disclaimer_visible: page.is_oath_disclaimer_visible,
    recurring_enabled: page.recurring_enabled,
    recurring_interval: page.recurring_interval,
    wire_enabled: page.wire_enabled,
    offline_instructions_wire: page.offline_instructions_wire || [],
    offline_instructions_wire_enabled: (page.offline_instructions_wire || []).length > 0,
    offline_instructions_check: page.offline_instructions_check || [],
    offline_instructions_check_enabled: (page.offline_instructions_check || []).length > 0,
    offline_instructions_grant: page.offline_instructions_grant || [],
    offline_instructions_grant_enabled: (page.offline_instructions_grant || []).length > 0,
    allocation_type: page.allocation_type || 'EVEN',
    recipient_groups:
      page.recipient_groups?.map((g: any) => ({
        id: isPageDuplicated ? null : g.id,
        page_id: isPageDuplicated ? null : g.page_id,
        weight_percent: g.weight_percent ?? 0,
        recipient_ids: g.recipient_ids || [],
        donation_limit_cents: g.donation_limit_cents ?? null,
        group_label: g.group_label || null,
      })) || [],
  };
};

/**
 * For duplicated pages: reset any field where canCreate is false to its
 * default value. Returns the modified values and a list of human-readable
 * field names that were reset (empty if nothing changed).
 */
export const resetRestrictedFields = (
  values: Record<string, any>,
  fieldRestrictions: IFieldRestrictions | undefined,
  preferredPaymentProcessor?: string
): { values: Record<string, any>; resetFieldNames: string[] } => {
  if (!fieldRestrictions) return { values, resetFieldNames: [] };

  const resetFieldNames: string[] = [];
  const patched = { ...values };

  const defaults: Record<string, any> = {
    ...RESTRICTED_FIELD_DEFAULTS,
    processing_redirect: preferredPaymentProcessor || 'STRIPE',
  };

  // whitelabeled is stored as 'TRUE'/'FALSE' string in form state
  const formDefaults: Record<string, any> = {
    ...defaults,
    whitelabeled: defaults.whitelabeled ? 'TRUE' : 'FALSE',
  };

  for (const [field, restriction] of Object.entries(fieldRestrictions)) {
    if (restriction.can_create) continue;
    if (!(field in formDefaults)) continue;
    if (patched[field] === formDefaults[field]) continue;

    patched[field] = formDefaults[field];
    resetFieldNames.push(RESTRICTED_FIELD_LABELS[field] || field);
  }

  return { values: patched, resetFieldNames };
};

export const convertUrlToFile = async (
  url: string,
  filename: string
): Promise<File | undefined> => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();

    // Ensure we have a valid blob
    if (blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }

    const file = new File([blob], filename, { type: blob.type || 'image/png' });
    return file;
  } catch {
    return undefined;
  }
};

export type WebsiteUrlCategory = 'page' | 'community';

const validateWebsiteUrlByCategory = (
  value: string,
  category: WebsiteUrlCategory,
  state: any,
  editSelected?: any,
  isDuplicated?: boolean
): string | undefined => {
  const requiredError = required(value);
  if (requiredError) return requiredError;
  const processedValue = transformToUrl(value);
  const currentValue = processedValue;
  const errorMessage = 'This URL is already being used.';

  if (!websiteWithLines.test(processedValue) || processedValue.endsWith('-')) {
    return 'This field has incorrect characters.';
  }
  if (category === 'page') {
    const pages = state?.data?.allPages?.pages;
    if (!pages) return undefined;
    const editItem = editSelected?.[0];
    return isInUseInList(pages, 'query_str', currentValue, editItem, isDuplicated)
      ? errorMessage
      : undefined;
  }
  /* category === 'community' */
  const sets = state?.data?.sets ?? [];
  return isInUseInList(sets, 'querystring', currentValue, editSelected, isDuplicated)
    ? errorMessage
    : undefined;
};

export const validateWebsiteUrl = (
  value: string,
  pagesState: any,
  editPageSelected?: any,
  isPageDuplicated?: boolean
) => validateWebsiteUrlByCategory(value, 'page', pagesState, editPageSelected, isPageDuplicated);

/** Validates set website URL / querystring (community category). */
export const validateSetWebsiteUrl = (
  value: string,
  setsState: any,
  editSetSelected?: any,
  isSetDuplicated?: boolean
) => validateWebsiteUrlByCategory(value, 'community', setsState, editSetSelected, isSetDuplicated);

export const validateStartTime = (value: any, allValues?: any) => {
  const requiredError = required(value);
  if (requiredError) return requiredError;
  const timeValue = typeof value === 'string' ? value : value?.target?.value || '';
  // Validate time format
  const timeMoment = moment(timeValue, 'LT');
  if (!timeMoment.isValid()) {
    return 'Invalid time format';
  }

  // Cross-field validations
  if (allValues?.end_time) {
    const endMoment = moment(allValues.end_time, 'LT');
    if (timeMoment.isAfter(endMoment)) {
      return 'The start time must be before the end time';
    }
  }

  return undefined;
};

export const validateEndTime = (value: any, allValues?: any) => {
  const timeValue = typeof value === 'string' ? value : value?.target?.value || '';
  const timeMoment = moment(timeValue, 'LT');

  // Cross-field validations
  if (allValues?.start_time) {
    const startMoment = moment(allValues.start_time, 'LT');
    if (startMoment.isAfter(timeMoment)) {
      return 'The end time value must be higher than the start time';
    }
  }

  return undefined;
};

export const validateDoorsOpenTime = (value: any, allValues?: any) => {
  const timeValue = typeof value === 'string' ? value : value?.target?.value || '';
  const timeMoment = moment(timeValue, 'LT');

  if (!timeValue && allValues?.doors_open_before_event) {
    return 'This field is required.';
  }

  // Cross-field validations
  if (allValues?.doors_open_before_event && allValues?.start_time) {
    const startMoment = moment(allValues.start_time, 'LT');
    if (timeMoment.isAfter(startMoment)) {
      return 'The doors open time value must be lower than the start time';
    }
  }

  return undefined;
};

const isInUseInList = (
  items: any[],
  fieldKey: string,
  currentValue: string,
  editItem: any,
  isDuplicated?: boolean
): boolean => {
  if (!items?.length) return false;
  const isEditingSame = editItem && editItem[fieldKey] === currentValue && !isDuplicated;
  if (isEditingSame) return false;
  return items.some((item: any) => item?.[fieldKey]?.toLowerCase() === currentValue?.toLowerCase());
};

type ImageDuplicationConfig = { url: string; filename: string; fieldName: string };

export const getImageConfigForDuplication = (page: any): ImageDuplicationConfig[] => {
  const gcsTagsBase = `${ENV_CONFIG().CLOUD_STORAGE.URL}/${ENV_CONFIG().CLOUD_STORAGE.TAGS}`;
  const configs: ImageDuplicationConfig[] = [];

  if (page?.has_image) {
    const filename = page.image_filenames?.principal_logo ?? `tag_${page.id}.png`;
    configs.push({ url: `${gcsTagsBase}/${filename}`, filename, fieldName: 'selectedFile' });
  }

  if (page?.has_community_image) {
    const filename = page.image_filenames?.community_logo ?? `tag_${page.id}.png`;
    configs.push({
      url: `${gcsTagsBase}/icon/${filename}`,
      filename,
      fieldName: 'selectedCommunityFile',
    });
  }

  if (page?.has_optional_media) {
    const ext = page.optional_media_type === 'video' ? 'mp4' : 'png';
    const filename = page.image_filenames?.optional_media ?? `tag_${page.id}.${ext}`;
    configs.push({
      url: `${gcsTagsBase}/optional_media/${filename}`,
      filename,
      fieldName: 'selectedOptionalMediaFile',
    });
  }

  return configs;
};

export const getPagePreviewUrl = (category: string, queryString: string) => {
  if (category === 'event') {
    return `${ENV_CONFIG().EVENT_PAGE}${queryString}`;
  }
  if (category === 'community') {
    return `${ENV_CONFIG().COMMUNITY_PAGE}${queryString}`;
  }
  return `${ENV_CONFIG().PARTNER_PAGE}${queryString}`;
};

export const getPageActions = (
  category: string,
  activeRole: string,
  handleUpdate: (type: string, param: string, pageId?: string) => void,
  prop: any,
  subscriptionLevel: string
) => {
  const editDuplicateParam = category === 'community' ? prop.set_id : prop.id;
  const permission = category === 'community' ? 'COMMUNITY_PAGES_ROUTE' : 'PAGES_ROUTE';
  return (
    <div className="grid-column">
      <div className="table-action-row">
        {has_permission(permission, 'U', activeRole, subscriptionLevel) &&
          tableButton(handleUpdate, 'edit', editDuplicateParam)}
        {tableButton(
          (type: string, queryString: string) =>
            handleUpdate(type, queryString, String(editDuplicateParam)),
          'preview',
          category === 'community' ? prop.querystring : prop.query_str
        )}
      </div>
      <div className="table-action-row margin0">
        {has_permission(permission, 'U', activeRole, subscriptionLevel) &&
          tableButton(handleUpdate, 'duplicate', editDuplicateParam)}
        {has_permission(permission, 'D', activeRole, subscriptionLevel) &&
          category !== 'community' &&
          tableButton(handleUpdate, 'delete', prop.id)}
      </div>
    </div>
  );
};

/**
 * Hook that returns a stable handleUpdate for preview/edit/duplicate.
 * Use this in both Pages and CommunityPages so they share the same behavior.
 */
export const useHandlePageUpdate = (category: string) => {
  const navigate = useNavigate();
  return useCallback(
    (type: string, param: string, pageId: string = param) => {
      trackPageRowActionClicked({
        screen: SCREEN,
        page_category: category as PageCategory,
        action: type as 'edit' | 'delete' | 'preview' | 'duplicate',
        page_id: String(pageId),
      });
      const previewURL = getPagePreviewUrl(category, param);
      if (type === 'preview') window.open(previewURL, '_blank');
      if (type === 'edit' || type === 'duplicate') {
        const baseUrl = category === 'community' ? '/community_pages_detail' : '/pages_detail';
        const idParam = category === 'community' ? `set_id=${param}` : `tag_id=${param}`;
        const queryParams = `${idParam}&page_type=${category}`;
        const copyParam = type === 'duplicate' ? '&copy' : '';
        navigate(`${baseUrl}?${queryParams}${copyParam}`);
      }
    },
    [category, navigate]
  );
};

/** Normalizes set_pages from API (object or array of query_strings) to { [query_str]: true }. */
export const normalizeSetPages = (val: unknown): Record<string, unknown> => {
  if (Array.isArray(val)) {
    return val.reduce(
      (acc: Record<string, unknown>, item: unknown) => {
        if (typeof item === 'string') acc[item] = true;
        return acc;
      },
      {} as Record<string, unknown>
    );
  }
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    return val as Record<string, unknown>;
  }
  return {};
};

export const getInitialSetValues = (editSetSelected?: SetsData, isSetDuplicated?: boolean) => {
  const misc = editSetSelected?.set_misc;

  const today = new Date();
  const defaultStartDate = toDateTimeString(today);
  const defaultEndDate = `${today.getFullYear() + 1}-01-01T00:00:00`;

  if (!editSetSelected) {
    return {
      set_title: '',
      set_desc: '',
      querystring: '',
      display_fundraising_bar: true,
      hide_recent_location: false,
      goal_alt: '',
      donate_custom_1: '',
      donate_custom_1_helper: '',
      show_header_button: true,
      header_bg_color: '',
      primary_color: '',
      secondary_color: '',
      tertiary_color: '',
      card_raised_alt: '',
      card_goal_alt: '',
      card_helper_text: '',
      edge_color: '',
      cta_color: '',
      set_pages: {},
      page_type: 'DONATION',
      sections: [],
      is_enabled: true,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      whitelabeled: 'FALSE',
      share_alt: '',
      share_disclaimer: '',
      share_title: '',
      share_description: '',
      has_share: false,
    };
  }

  return {
    set_title: isSetDuplicated ? `${editSetSelected.set_title} Copy` : editSetSelected.set_title,
    set_desc: editSetSelected.set_desc ?? '',
    querystring: isSetDuplicated
      ? `${editSetSelected.querystring}-copy`
      : (editSetSelected.querystring ?? ''),
    display_fundraising_bar: editSetSelected.display_fundraising_bar ?? true,
    startDate: isSetDuplicated
      ? defaultStartDate
      : (editSetSelected.start_date ?? defaultStartDate),
    endDate: isSetDuplicated ? defaultEndDate : (editSetSelected.end_date ?? defaultEndDate),
    hide_recent_location: misc?.hide_recent_location ?? false,
    goal_alt: misc?.goal_alt ?? '',
    donate_custom_1: misc?.donate_custom_1 ?? '',
    donate_custom_1_helper: misc?.donate_custom_1_helper ?? '',
    show_header_button: misc?.show_header_button ?? true,
    header_bg_color: misc?.header_bg_color ?? '',
    primary_color: misc?.primary_color ?? '',
    secondary_color: misc?.secondary_color ?? '',
    tertiary_color: misc?.tertiary_color ?? '',
    card_raised_alt: misc?.issue_misc?.card_raised_alt ?? '',
    card_goal_alt: misc?.issue_misc?.card_goal_alt ?? '',
    card_helper_text: misc?.issue_misc?.card_helper_text ?? '',
    edge_color: misc?.issue_misc?.edge_color ?? '',
    cta_color: misc?.issue_misc?.cta_color ?? '',
    set_pages: normalizeSetPages(editSetSelected.set_pages ?? {}),
    page_type: 'DONATION',
    is_enabled: editSetSelected.is_enabled ?? true,
    whitelabeled: misc?.whitelabeled ? 'TRUE' : 'FALSE',
    share_alt: misc?.share_alt ?? '',
    share_disclaimer: misc?.share_disclaimer ?? '',
    share_title: misc?.share_title ?? '',
    share_description: misc?.share_description ?? '',
    has_share: misc?.has_share ?? false,
    ...(isSetDuplicated
      ? {
          sections: (misc?.sections ?? []).map((s: any) => ({
            ...createEmptySection(),
            ...s,
          })),
        }
      : (() => {
          const gcsBase = `${ENV_CONFIG().CLOUD_STORAGE.URL}/${ENV_CONFIG().CLOUD_STORAGE.SETS}`;
          const qs = editSetSelected.querystring;
          const imageFilenames = editSetSelected.image_filenames;
          return {
            previewVideo: misc?.has_header_video
              ? imageFilenames?.header_video
                ? `${gcsBase}/${imageFilenames.header_video}`
                : `${gcsBase}/${qs}/header.mp4`
              : '',
            previewLogo: misc?.has_header_logo
              ? imageFilenames?.header_logo
                ? `${gcsBase}/${imageFilenames.header_logo}`
                : `${gcsBase}/${qs}/set-logo.png`
              : '',
            sections: (misc?.sections ?? []).map((s: any) => ({
              ...createEmptySection(),
              ...s,
              previewSection: s.has_image
                ? imageFilenames?.[`section:${s.id}`]
                  ? `${gcsBase}/${imageFilenames[`section:${s.id}`]}`
                  : `${gcsBase}/${qs}/section-${s.id}-logo.png`
                : '',
            })),
          };
        })()),
  };
};

export type AllPagesByType = {
  donate: IPage[];
  event: IPage[];
  recommendation: IPage[];
};

export const validateSetPagesRequired = (
  value: Record<string, unknown> | undefined
): string | undefined => {
  const normalized = normalizeSetPages(value);
  if (Object.keys(normalized).length === 0) {
    return 'At least one page is required';
  }
  return undefined;
};

export const validateCommunityPageForm = (values: any) => {
  const errors: Record<string, string | undefined> = {
    set_pages: validateSetPagesRequired(values?.set_pages),
  };
  if (
    values?.endDate &&
    values?.startDate &&
    new Date(values.endDate) <= new Date(values.startDate)
  ) {
    errors.endDate = 'End date must be after start date';
  }
  return errors;
};

export const flattenPagesByType = (allPagesByType: AllPagesByType) => {
  return [
    ...(allPagesByType.donate ?? []),
    ...(allPagesByType.event ?? []),
    ...(allPagesByType.recommendation ?? []),
  ];
};
